import { Component, NgZone, ChangeDetectorRef, Input, EventEmitter, ViewEncapsulation } from '@angular/core';
import { LanguageSettings } from '@common/services/language-settings'
import { ExternalActionsService } from '@common/services/external-actions-service'
import { TopicRecord } from '../../data-models/records/topic-record';
import { ExternalAction, ExternalActionResponse } from '@src/suite/common/data-models/service/external-action';
import { AppService } from '@src/suite/app.service';
import { PlaybookService } from '../../services/playbook-service';
import { ClipboardTypes } from '@src/suite/common/enums/clipboard-types';
import { MatDialog } from '@angular/material/dialog';
import { CreateNewTemplate } from '@src/suite/common/components/create-new-template/create-new-template';
import { PlaybookSearchResults } from '../../data-models/playbook-search-results/playbook-search-results';
import { DataStore } from '@src/suite/common/data-models/data-store/data-store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { AssignmentRecord } from '../../data-models/records/assignment-record';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ServiceResponse } from '@src/suite/common/data-models/service/service-response';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { MatSelectionListChange } from '@angular/material/list';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';

@Component({
    selector: 'external-actions-overlay',
    templateUrl: './external-actions-overlay.html',
    styleUrls: ['./external-actions-overlay.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        'class': 'st-external-actions-overlay'
    }
})
export class ExternalActionsOverlay {
    @Input('data') public data: TopicRecord | AssignmentRecord;
    @Input('editable') public editable: boolean;
    @Input('isPanelOpen') public isPanelOpen: boolean;
    @Input() public onObjectOpen: EventEmitter<any> = new EventEmitter();
    @Input() public onDelete: EventEmitter<any> = new EventEmitter();
    isNew: boolean = false;
    public currentAction: string = "";
    public currentActionError = false;
    public currentActionCompleted = false;


    @Input()
    get inFullScreen() {
        return this._inFullScreen;
    }
    set inFullScreen(value: BooleanInput) {
        this._inFullScreen = coerceBooleanProperty(value);
    }
    private _inFullScreen = false;

    constructor(
        public ngZone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
        public languageSettings: LanguageSettings,
        public externalActionsService: ExternalActionsService,
        public appService: AppService,
        public playbookService: PlaybookService,
        protected dialog: MatDialog,
        public modalService: ModalService,
        private nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
    ) {

    }
    get openFullScreenActionButtonLabel() {
        const actionLabel = 'PSTWebButton';
        return this.languageSettings.get(`${actionLabel}_${this.languageSettings.PSTWebOpenFullScreenText}`).includes(actionLabel) ? this.languageSettings.PSTWebOpenFullScreenText : this.languageSettings.get(`${actionLabel}_${this.languageSettings.PSTWebOpenFullScreenText}`)
    }

    get openFullScreenActionButtonHoverTitle() {
        const actionLabel = 'PSTWebButtonHT';
        return this.languageSettings.get(`${actionLabel}_${this.languageSettings.PSTWebOpenFullScreenText}`).includes(actionLabel) ? this.languageSettings.PSTWebOpenFullScreenText : this.languageSettings.get(`${actionLabel}_${this.languageSettings.PSTWebOpenFullScreenText}`)
    }

    get actions(): ExternalAction[] {
        if (this.appService.navigationService.Application == 'cast') {
            return this.externalActionsService.getExternalActionsForType(ObjectTypes.PUBLICATIONLIST)
        } else {
            return this.externalActionsService.getExternalActionsFor(this.data);
        }
    }

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    getExternaActionsLabel(action: ExternalAction) {
        const actionLabel = 'PSTWebButton';
        return this.languageSettings.get(`${actionLabel}_${action.ID}`).includes(actionLabel) ? action.Description : this.languageSettings.get(`${actionLabel}_${action.ID}`);
    }

    getExternaActionsHoverTitle(action: ExternalAction) {
        const actionLabel = 'PSTWebButtonHT';
        return this.languageSettings.get(`${actionLabel}_${action.ID}`).includes(actionLabel) ? action.Description : this.languageSettings.get(`${actionLabel}_${action.ID}`);
    }

    openNewTemplateDialog() {
        this.dialog.closeAll();
        const dialogRef = this.dialog.open(CreateNewTemplate, {
            panelClass: 'create-new-template',
            width: '22rem',
            height: '22rem',
            hasBackdrop: true,
            data: { data: this.data }
        });
    }

    copyToClipboard() {
        this.appService.clipboard.copy(this.data, ClipboardTypes.Topic)
    }

    copyObjectUrlToClipboard() {
        this.appService.clipboard.copy(this.data, ClipboardTypes.TopicUrl, 'playbook')
    }

    async saveAsTemplate() {
        this.appService.navigationService.saveTemplateSettings(this.data);
    }

    async cloneItem() {
        if (this.data.type === ObjectTypes.TOPIC && this.data instanceof TopicRecord) {
            await this.playbookService.duplicateItem(false, this.data, this.isNew);
        }
        if (this.data.type === ObjectTypes.ASSIGNMENT && this.data instanceof AssignmentRecord) {
            await this.playbookService.duplicateAssignment(this.data, <TopicRecord>this.data.parentRecord, false);
        }
    }

    async duplicateItem() {
        if (this.data.type === ObjectTypes.TOPIC && this.data instanceof TopicRecord) {
            await this.playbookService.duplicateItem(true, this.data, this.isNew);
        }
        if (this.data.type === ObjectTypes.ASSIGNMENT && this.data instanceof AssignmentRecord) {
            await this.playbookService.duplicateAssignment(this.data, <TopicRecord>this.data.parentRecord, true);
        }
    }

    actionChanged(event?: MatSelectionListChange) {
        const actionId: string = event.source._value[0];
        const action = this.actions.find(a => a.ID == actionId);
        if (action?.ID !== "CLONE" && action?.ID !== "DUPLICATE" && action?.ID !== "DELETE" && action?.ID !== "COPYLINK" && action?.ID !== "TEMPLATE" && action?.ID !== "COPYTOCLIPBOARD") {
            this.currentAction = action?.ID;
        }

        if (actionId != null) {
            switch (actionId) {
                case 'DUPLICATE':
                    this.duplicateItem();
                    break;
                case 'DELETE':
                    this.deleteItem();
                    break;
                case 'CLONE':
                    this.cloneItem();
                    break;
                case 'COPYLINK':
                    this.copyObjectUrlToClipboard();
                    break;
                case 'TEMPLATE':
                    this.openNewTemplateDialog();
                    break;
                case 'COPYTOCLIPBOARD':
                    this.copyToClipboard();
                    break;
                default:
                    this.executeAction(action);
                    break;
            }
        }
        this.isPanelOpen = false;
    }

    getIcon(action): string {
        if (action.Icon != null) {
            switch (action?.Icon) {
                case 'DUPLICATE':
                    return 'file_copy';
                case 'DELETE':
                    return 'delete';
                case 'CLONE':
                    return 'file_copy';
                case 'COPY LINK':
                    return 'link';
                case 'TEMPLATE':
                    return 'wysiwyg';
                case 'COPYTOCLIPBOARD':
                    return 'content_paste';
                default:
                    break;
            }
        }
    }
    async deleteItem() {
        const modalContent = this.languageSettings.PSTWebConfirmDeletePart1 + this.data?.title + (this.data.type == 137 ? this.languageSettings.PSTWebConfirmDeleteTopicPart2 : this.languageSettings.PSTWebConfirmDeleteAssignmentPart2);
        let modalRef = this.modalService.openModal(this.languageSettings.PSTWebConfirmDeleteLabel, modalContent, this.languageSettings.PSTWebYesOption, this.languageSettings.PSTWebNoOption);
        let modalRes;
        try {
            modalRes = await modalRef.result;
        } catch (er) {
            modalRes = ModalResponseCodes.CANCEL;
        }
        if (modalRes == ModalResponseCodes.OK) {
            if (!this.data?.guid) {
                if (this.data?.type == ObjectTypes.ASSIGNMENT) {
                    const idx = this.data?.parentRecord?.children?.indexOf(this.data);
                    this.data.parentRecord.removeChildByRef(this.data);
                    this.onDelete.emit(idx);
                    return;
                }
            }
            this.appService?.navigationService?.currentListView.showLoader();
            let res = await this.appService.basicContentService.deleteBasicContent(this.data?.database, this.data?.path.fullPath, this.data?.guid, this.data?.type, null);
            if (res.status) {
                this.playbookService.playbookSearchResults.remove(this.data);
                PlaybookSearchResults.newlyAddedItems.remove(this.data);
                if (this.appService.navigationService.currentListView.tileView) {
                    this.appService.navigationService.currentListView.tileView.startIndex = 0;
                    this.appService.navigationService.currentListView.tileView.pageSize = 7;
                }
                let currentRecord: TopicRecord | AssignmentRecord = DataStore.byGuid<TopicRecord | AssignmentRecord>(this.data?.guid, "");
                if (currentRecord) {
                    DataStore.disposeRecord(currentRecord, true);
                }
                this.onDelete.emit();
            } else {
                if (res.message && res.message.indexOf("CAST_LINK_VIOLATION") != -1) {
                    let ind = res.message.indexOf('Details: ')
                    let msg = res.message.substring(ind + 9, res.message.length - 1)
                    this.modalService.openAlert(this.languageSettings.PSTWebErrorDeletingLabel, this.languageSettings.PSTWebCastLinkViolationErrorLabel + " \n" + msg, null);
                }
            }
            this.appService?.navigationService?.currentListView.hideLoader();
        }
    }

    public async executeAction(action: ExternalAction) {
        this.currentActionError = false;
        this.currentActionCompleted = false;
        let res: ServiceResponse<ExternalActionResponse> = await this.externalActionsService.postExternalAction(this.data.getExternalApiData(action.ID, this.nomWebServicesJson.UserId));
        if (!res) {
            this.currentActionError = true;
        }
        else {
            try {
                if (res.data && res.data.status == 200) {
                    this.currentActionCompleted = true;
                } else {
                    this.currentActionError = true;
                }
            } catch (e) {
                this.currentActionError = true;
                console.log(e);
            }
        }
    }
}
